import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Stack, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
  PageHeader,
  SearchFilterBar,
  DataTable,
  StatusChip,
  flattenPages,
} from '../components';
import { useReservationsPaginated } from '../hooks/useStockReservations';
import { colors, formatDate } from '../constants';

export default function StockReservations() {
  const history = useHistory();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const params = useMemo(
    () => ({
      limit: 20,
      status: statusFilter || undefined,
      search: search || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    }),
    [statusFilter, search, dateFrom, dateTo]
  );

  const query = useReservationsPaginated(params);
  const items = flattenPages(query);

  const columns = [
    {
      key: 'orderReference',
      label: 'Reference',
      render: (row) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: colors.navy }}>
          {row.orderReference}
        </Typography>
      ),
    },
    { key: 'items', label: 'Items', align: 'right', render: (row) => row.items?.length || 0 },
    { key: 'createdAt', label: 'Date', render: (row) => formatDate(row.createdAt) },
    { key: 'status', label: 'Status', render: (row) => <StatusChip status={row.status} /> },
  ];

  return (
    <>
      <PageHeader
        title="Stock Reservations"
        actionLabel="New Reservation"
        actionIcon={<AddIcon />}
        onAction={() => history.push('/inventory-management/stock-reservations/new')}
      />

      <SearchFilterBar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search reservations..."
        filters={[
          {
            label: 'Status',
            value: statusFilter,
            onChange: setStatusFilter,
            options: [
              { value: 'pending', label: 'Pending' },
              { value: 'fulfilled', label: 'Fulfilled' },
              { value: 'released', label: 'Released' },
              { value: 'expired', label: 'Expired' },
            ],
          },
        ]}
      >
        <Stack direction="row" spacing={1}>
          <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} sx={{ backgroundColor: '#fff' }} />
          <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={dateTo} onChange={(e) => setDateTo(e.target.value)} sx={{ backgroundColor: '#fff' }} />
        </Stack>
      </SearchFilterBar>

      <DataTable
        columns={columns}
        rows={items}
        loading={query.isLoading}
        onRowClick={(row) => history.push(`/inventory-management/stock-reservations/${row.uuid}`)}
        emptyTitle="No reservations"
        hasNextPage={query.hasNextPage}
        isFetchingNextPage={query.isFetchingNextPage}
        fetchNextPage={query.fetchNextPage}
      />
    </>
  );
}
